import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import CharacterCount from "@tiptap/extension-character-count";

import RichTextToolbar from "./RichTextToolbar";

const DEFAULT_MAX_CHARACTERS = 2000;

interface RichTextEditorProps {
  content: string;
  placeholder: string;
  onUpdate: (content: string) => void;
  onCharacterCount: (count: number) => void;
  maxCharacters?: number;
  // Fired when a paste exceeds maxCharacters (TipTap truncates it to the limit).
  onLimitExceeded?: () => void;
}

/**
 * TipTap-based rich text editor.
 *
 * The editor instance is created once and content is swapped via
 * `setContent()` on external content changes — the editor is never
 * re-initialized.
 */
const RichTextEditor = ({
  content,
  placeholder,
  onUpdate,
  onCharacterCount,
  maxCharacters = DEFAULT_MAX_CHARACTERS,
  onLimitExceeded,
}: RichTextEditorProps): React.JSX.Element => {
  const isSettingContent = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      CharacterCount.configure({ limit: maxCharacters }),
    ],
    content,
    editorProps: {
      attributes: {
        class: "rich-text-editor",
        "data-placeholder": placeholder,
        "data-testid": "rich-text-editor",
      },
      handlePaste: (view, event) => {
        if (!onLimitExceeded) return false;
        const pastedText = event.clipboardData?.getData("text/plain") ?? "";
        const { from, to } = view.state.selection;
        const replacedLength = view.state.doc.textBetween(from, to).length;
        const usedLength = view.state.doc.textContent.length - replacedLength;
        if (pastedText.length > maxCharacters - usedLength) {
          onLimitExceeded();
        }
        // Let TipTap apply and truncate at the limit.
        return false;
      },
    },
    onUpdate: ({ editor: editorInstance }) => {
      if (isSettingContent.current) return;
      const html = editorInstance.getHTML();
      onUpdate(html);
      onCharacterCount(
        editorInstance.storage.characterCount.characters() as number,
      );
    },
  });

  // Swap content without destroying the editor when the source content changes
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const currentHtml = editor.getHTML();
    if (currentHtml === content) return;

    isSettingContent.current = true;
    editor.commands.setContent(content, { emitUpdate: false });
    isSettingContent.current = false;
    onCharacterCount(editor.storage.characterCount.characters() as number);
  }, [content, editor, onCharacterCount]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <RichTextToolbar editor={editor} />
      <Box
        // Focus when clicking anywhere in the padded area, not just the text.
        onClick={() => editor?.chain().focus().run()}
        sx={{
          flex: 1,
          overflow: "auto",
          px: 2,
          py: 1,
          cursor: "text",
          "& > div": {
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
          },
          "& .rich-text-editor": {
            outline: "none",
            flex: 1,
            minHeight: "100%",
            fontFamily: "inherit",
            fontSize: "0.875rem",
            lineHeight: 1.6,
          },
          "& .rich-text-editor p.is-editor-empty:first-of-type::before": {
            content: "attr(data-placeholder)",
            color: "text.disabled",
            float: "left",
            height: 0,
            pointerEvents: "none",
          },
          "& .rich-text-editor:focus": {
            outline: "none",
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
